import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useAuthStore } from "../../stores/authStore";
import { authCallback } from "@/services/api/auth/auth";
import type { CallbackRequest } from "@/types/api/callbackRequest";

type AuthCallbackResult = {
  success?: boolean;
  errorCode?: string;
};

const isAuthCallbackResult = (value: unknown): value is AuthCallbackResult =>
  typeof value === "object" && value !== null;

export const Route = createFileRoute("/auth/callback")({
  component: AuthCallback,
});

function AuthCallback() {
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const processedRef = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      if (processedRef.current) return;
      processedRef.current = true;

      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");

      if (code && state) {
        try {
          const payload: CallbackRequest = { code, state };
          const rawResult: unknown = await authCallback(payload);
          const callbackResult = isAuthCallbackResult(rawResult) ? rawResult : undefined;

          if (callbackResult?.success) {
            // Refresh auth state after successful login
            await checkAuth();
            // Redirect to my-learning dashboard
            window.location.href = "/my-learning";
          } else {
            const errorCode = callbackResult?.errorCode ?? "auth_failed";
            window.location.href = `/login?error=${encodeURIComponent(errorCode)}`;
          }
        } catch (error) {
          console.error("Authentication error:", error);
          window.location.href = "/login?error=auth_error";
        }
      } else {
        const error = !code ? "no_code" : "no_state";
        window.location.href = `/login?error=${error}`;
      }
    };

    handleCallback();
  }, [checkAuth]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
        <p className="text-gray-600 dark:text-gray-400">Authenticating...</p>
      </div>
    </div>
  );
}
